import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAppAuth, useAppTenantsState } from '../hooks/useAppAuth';
import { useEffect, useState } from 'react';
import Sidebar from '../components/Sidebar';
import { SidebarProvider, SidebarInset } from '../components/ui/sidebar';
import AuthService from '../services/auth.service';
import FeatureFlagService from '../services/featureFlag.service';
import { FeatureFlag, useFeatureFlag } from '../services/featureFlag.service';
import { useWhitelabelOverrides } from '../hooks/useWhitelabelOverrides';
import { UnsavedChangesProvider } from '../contexts/UnsavedChangesContext';
import { useTenantConfig } from '../hooks/useTenantConfig';
import { ApiError } from '../services/api.service';

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

export default function AuthenticatedRootPage() {
  const { isAuthenticated: fronteggAuthenticated, isLoading, user } = useAppAuth();
  const { tenants } = useAppTenantsState();
  const location = useLocation();
  useWhitelabelOverrides();
  const { data: tenantConfig, error: configError, isError: isConfigError } = useTenantConfig();

  // Check for system session auth (used by screengrabber/notifications)
  const hasSystemSession = !fronteggAuthenticated && !!AuthService.getInstance().getAuthState();
  const isAuthenticated = fronteggAuthenticated || hasSystemSession;

  // Sync Frontegg auth state with AuthService
  useEffect(() => {
    if (user && isAuthenticated) {
      // Create auth state from Frontegg
      // Map IRole[] to string[] for roles
      const roleNames = (user.roles || []).map(role =>
        typeof role === 'string' ? role : role.name || role.key || ''
      );
      const authState = {
        sub: user.id || '',
        name: user.name || '',
        email: user.email || '',
        tenant_id: user.tenantId || '',
        roles: roleNames,
        tenant_name: user.tenantId || 'Tenant',
        access_token: user.accessToken || '',
        profile_img_url: user.profilePictureUrl || undefined,
        tenants: [],
        customClaims: (user as any).customClaims || {},
      };
      AuthService.getInstance().setAuthState(authState);
      const currentTenant = tenants?.find((t: any) => t.tenantId === user.tenantId);
      const lowerRoles = roleNames.map(r => r.toLowerCase());
      const highestRole = lowerRoles.includes('admin') ? 'Admin'
        : lowerRoles.includes('builder') ? 'Builder'
        : lowerRoles.includes('readonly') ? 'ReadOnly'
        : lowerRoles.includes('readonly_partner') ? 'ReadOnly_Partner'
        : 'Unknown';
      FeatureFlagService.getInstance().identify({
        key: user.id || '',
        name: user.name || '',
        email: user.email || '',
        tenant: currentTenant?.name || 'Tenant',
        role: highestRole,
      });
    }
  }, [user, isAuthenticated]);

  // After SSO callback, check if we need to redirect back to a custom domain.
  // Track redirect state so we show a loading screen instead of flashing the app.
  const [redirectingToCustomDomain, setRedirectingToCustomDomain] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;

    // ?nocustom=1 escape hatch — skip custom domain redirect entirely
    const params = new URLSearchParams(window.location.search);
    if (params.get('nocustom') === '1') return;

    // If the tenant has a custom domain configured and we're not already
    // on it, redirect there. This replaces the fragile cookie/Redis approach
    // which was broken by Safari ITP stripping cookies during redirects.
    const customDomain = (tenantConfig as any)?.custom_domain;
    const customDomainToken = (tenantConfig as any)?.custom_domain_token;
    if (customDomain && customDomainToken) {
      const customOrigin = `https://${customDomain}`;
      if (customOrigin !== window.location.origin) {
        setRedirectingToCustomDomain(true);

        const doRedirect = (authCode?: string) => {
          let redirectUrl = `/api/v1/env/return?url=${encodeURIComponent(customOrigin)}&token=${encodeURIComponent(customDomainToken)}`;
          if (authCode) {
            redirectUrl += `&auth_code=${encodeURIComponent(authCode)}`;
          }
          window.location.href = redirectUrl;
        };

        if (user?.accessToken) {
          fetch('/api/v1/env/auth-handoff', {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${user.accessToken}` },
          })
            .then(r => r.ok ? r.json() : null)
            .then(data => doRedirect(data?.code))
            .catch(() => doRedirect());
        } else {
          doRedirect();
        }
      }
    }
  }, [isAuthenticated, tenantConfig]);

  // Redirect authenticated users to legacy frontend when force-legacy flag is enabled
  const forceLegacy = useFeatureFlag(FeatureFlag.FORCE_LEGACY);
  useEffect(() => {
    const authService = AuthService.getInstance();
    if (forceLegacy && authService.legacyAppUrl && isAuthenticated) {
      window.location.href = authService.legacyAppUrl;
    }
  }, [forceLegacy, isAuthenticated]);

  // Store return path for redirect after login
  useEffect(() => {
    if (location && !location.pathname.includes('/login') && !location.pathname.includes('/logout')) {
      const existingReturnPath = localStorage.getItem('returnPath');
      if (!existingReturnPath && location.hash !== '#returning') {
        localStorage.setItem('returnPath', location.pathname + location.search);
      }
    }
  }, [location]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    const authService = AuthService.getInstance();
    if (authService.useExternalLogin) {
      // Locally, Frontegg OAuth is configured for a different port, so redirect there for login
      window.location.href = `${authService.authAppUrl}/login`;
      return null;
    }
    return <Navigate to="/login" replace />;
  }

  // Show loading screen while redirecting to custom domain, OR while
  // waiting for config to load so we can check if a redirect is needed.
  // Without this, the main domain app flashes briefly before redirecting.
  if (redirectingToCustomDomain || (!AuthService.getInstance().isCustomDomain && isAuthenticated && !tenantConfig)) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (isConfigError && configError instanceof ApiError && configError.code === 404) {
    const payload = configError.payload as any;
    if (payload?.archived) {
      return <Navigate to="/tenant-archived" state={{ tenantName: payload.tenant_name }} replace />;
    }
    return <Navigate to="/tenant-not-found" replace />;
  }

  return (
    <SidebarProvider>
      <Sidebar />
      <SidebarInset>
        <UnsavedChangesProvider>
          <Outlet />
        </UnsavedChangesProvider>
      </SidebarInset>
    </SidebarProvider>
  );
}

